import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Lock, Download, FileText } from "lucide-react";
import { api, API, formatErr } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ThemeToggle, Footer, StatusBadge } from "@/components/sg/Brand";

function ApproveCard({ token, m, onDone, lateNote }) {
  const [signer, setSigner] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/portal/${token}/approve`, { milestone_id: m.id, signer_name: signer });
      onDone(r.data);
      toast.success("Milestone approved. Thank you!");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border rounded-xl bg-card p-6" data-testid="portal-action-card">
      <div className={`text-xs uppercase tracking-widest font-semibold ${m.status === "overdue" ? "text-red-600 dark:text-red-400" : "text-amber-700 dark:text-amber-400"}`}>Action required</div>
      <h2 className="font-display text-xl font-bold mt-1">{m.name}</h2>
      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{m.description}</p>
      {m.status === "overdue" && lateNote && (
        <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/5 p-3 text-sm text-red-700 dark:text-red-400" data-testid="portal-late-fee-note">
          <span className="font-semibold">Please note:</span> {lateNote}
        </div>
      )}
      <div className="mt-4 border-t border-border pt-4 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Amount due on approval</div>
        <div className="font-display text-2xl font-bold tabular-nums" data-testid="portal-amount-due">{money(m.amount)}</div>
      </div>
      <div className="mt-5">
        <Label>Type your full name to sign</Label>
        <Input data-testid="portal-signer-input" value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Full name" className="mt-1 h-11" />
      </div>
      <label className="flex items-start gap-2 mt-3 text-sm text-muted-foreground cursor-pointer">
        <Checkbox data-testid="portal-agree-checkbox" checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" />
        I confirm this milestone is complete as described and approve the amount above.
      </label>
      <Button data-testid="portal-approve-btn" disabled={busy || signer.trim().length < 2 || !agree} onClick={approve}
        className="w-full mt-5 h-12 bg-slate-900 hover:bg-slate-800 text-white dark:bg-indigo-600 dark:hover:bg-indigo-500 font-semibold">
        <Check className="h-4 w-4 mr-2" /> {busy ? "Signing…" : "Sign & Approve Milestone"}
      </Button>
    </div>
  );
}

function PayCard({ c, due }) {
  if (!due || (!c.payment_link && !c.payment_instructions)) return null;
  return (
    <div className="border border-border rounded-xl bg-card p-6 mt-5" data-testid="portal-pay-card">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Amount due</div>
      <div className="font-display text-2xl font-bold tabular-nums mt-1">{money(due)}</div>
      {c.payment_instructions && <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap" data-testid="portal-payment-instructions">{c.payment_instructions}</p>}
      {c.payment_link && (
        <a href={c.payment_link} target="_blank" rel="noopener noreferrer" data-testid="portal-pay-now-btn"
          className="mt-4 flex items-center justify-center h-12 rounded-md text-white font-semibold transition-opacity hover:opacity-90" style={{ background: c.brand_color || "#059669" }}>
          Pay now
        </a>
      )}
    </div>
  );
}

export function PortalView({ token, preview = false, standalone = false }) {
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get(`/portal/${token}`, { params: preview ? { preview: true } : {} }); setProject(r.data); setError(null); }
    catch (e) { setError(formatErr(e.response?.data?.detail)); }
  }, [token, preview]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="text-center text-muted-foreground py-20" data-testid="portal-error">{error}</div>;
  if (!project) return <div className="text-center text-muted-foreground py-20">Loading…</div>;
  const current = project.milestones.find((m) => ["awaiting_signature", "overdue"].includes(m.status));
  const from = project.contractor?.company_name || project.contractor?.display_name || "your contractor";
  const paidSum = project.milestones.filter((m) => m.status === "paid").reduce((a, m) => a + m.amount, 0);
  const c = project.contractor || {};
  const dueSum = project.milestones.filter((m) => m.status === "ready_to_invoice").reduce((a, m) => a + m.amount, 0);

  return (
    <>
    <div className="max-w-md mx-auto px-5 py-8" data-testid="client-portal" style={c.brand_color ? { "--brand": c.brand_color } : undefined}>
      {standalone && !c.white_label && <div className="text-xs text-muted-foreground -mt-6 mb-4" data-testid="portal-powered-by">powered by ScopeGuard</div>}
      <div className="border border-border rounded-xl bg-card p-6 mb-5" style={c.brand_color ? { borderTop: `4px solid ${c.brand_color}` } : undefined}>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Project from {from}</div>
        <h1 className="font-display text-2xl font-bold mt-1" data-testid="portal-project-name">{project.title || project.client_name}</h1>
        <div className="mt-4 border-t border-border pt-4 grid grid-cols-2 gap-4">
          <div><div className="text-xs text-muted-foreground">Contract total</div><div className="font-display text-2xl font-bold text-indigo-600 dark:text-indigo-400 tabular-nums">{money(project.total_amount)}</div></div>
          <div><div className="text-xs text-muted-foreground">Paid so far</div><div className="font-display text-2xl font-bold tabular-nums">{money(paidSum)}</div></div>
        </div>
      </div>

      {current ? <ApproveCard token={token} m={current} onDone={setProject} lateNote={c.late_fee_note} /> : (
        <div className="border border-emerald-500/40 rounded-xl bg-emerald-500/5 p-8 text-center" data-testid="portal-all-clear">
          <Check className="h-10 w-10 mx-auto text-emerald-600 dark:text-emerald-400 mb-3" />
          <h2 className="font-display text-xl font-bold">Nothing to sign right now</h2>
          <p className="text-sm text-muted-foreground mt-2">You'll get an email when the next milestone is ready.</p>
        </div>
      )}
      <PayCard c={c} due={dueSum} />

      <div className="mt-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">All milestones</div>
        <div className="space-y-2">
          {project.milestones.map((m, i) => (
            <div key={m.id} className="border border-border rounded-lg bg-card p-3" data-testid={`portal-milestone-${i}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0"><div className="font-medium text-sm truncate">{m.name}</div><div className="text-xs text-muted-foreground tabular-nums">{money(m.amount)}</div></div>
                <StatusBadge status={m.status} />
              </div>
              {["ready_to_invoice", "paid"].includes(m.status) && (
                <div className="mt-2 pt-2 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span>Signed by {m.signer_name} · {fmtDate(m.approved_at)}</span>
                  <a href={`${API}/portal/${token}/invoice/${m.id}.pdf`} className="inline-flex items-center gap-1 font-semibold text-indigo-600 dark:text-indigo-400 hover:underline" data-testid={`portal-invoice-${i}`}>
                    <Download className="h-3 w-3" /> Invoice
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {project.contractor?.showcase_slug && (
        <Link to={`/c/${project.contractor.showcase_slug}`} className="mt-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="portal-contractor-portfolio-link">
          <FileText className="h-4 w-4" /> See {from}'s portfolio
        </Link>
      )}
    </div>
    {standalone && !c.white_label && <Footer />}
    </>
  );
}

export default function ClientPortalPage() {
  const { token } = useParams();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-md mx-auto px-5 pt-6 flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-indigo-600 dark:text-indigo-400 font-semibold border border-indigo-500/40 rounded-full px-3 py-1" data-testid="portal-branding">
          <Lock className="h-3 w-3" /> Secure Project Portal
        </div>
        <ThemeToggle />
      </div>
      <PortalView token={token} standalone />
    </div>
  );
}
